import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { Loader2, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

/**
 * ResizableDataTable — reusable table with:
 *  - Drag-resizable column widths
 *  - Per-column text/dropdown filters (Excel-style)
 *  - Optional sorting
 *
 * Props:
 *  columns: Array of { key, label, width (initial px), minWidth, filterType ('text'|'select'|'number'|'date'|'none'), filterOptions: [{value, label}], sortable, render(row) }
 *  data: Array of row objects
 *  loading: boolean
 *  emptyMessage: string
 *  isDark: boolean
 *  rowKey: string|function — key extractor for rows
 *  onRowClick: (row) => void
 *  className: string — additional class for the Card wrapper
 */
const STORAGE_PREFIX = 'stadtki_table_';

export default function ResizableDataTable({
  columns,
  data = [],
  loading = false,
  emptyMessage = 'Keine Einträge gefunden.',
  isDark = false,
  rowKey = 'id',
  onRowClick,
  className,
  storageKey,
}) {
  // --- Column widths (resizable, optional persist) ---
  const initialWidths = useMemo(
    () => columns.reduce((acc, col) => ({ ...acc, [col.key]: col.width || 150 }), {}),
    [columns]
  );
  const [colWidths, setColWidths] = useState(() => {
    if (!storageKey) return initialWidths;
    try {
      const raw = localStorage.getItem(STORAGE_PREFIX + storageKey);
      if (!raw) return initialWidths;
      const saved = JSON.parse(raw);
      const merged = { ...initialWidths };
      columns.forEach((col) => {
        if (saved[col.key] != null && typeof saved[col.key] === 'number' && saved[col.key] >= (col.minWidth || 60)) {
          merged[col.key] = saved[col.key];
        }
      });
      return merged;
    } catch {
      return initialWidths;
    }
  });
  const resizingRef = useRef(null);
  const lastWidthsRef = useRef(null);

  const handleMouseDown = useCallback((e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = colWidths[colKey] || 150;
    resizingRef.current = { key: colKey, startX, startWidth };

    const onMouseMove = (ev) => {
      if (!resizingRef.current) return;
      const diff = ev.clientX - resizingRef.current.startX;
      const col = columns.find((c) => c.key === resizingRef.current.key);
      const minW = col?.minWidth || 60;
      const newWidth = Math.max(minW, resizingRef.current.startWidth + diff);
      setColWidths((prev) => {
        const next = { ...prev, [resizingRef.current.key]: newWidth };
        lastWidthsRef.current = next;
        return next;
      });
    };

    const onMouseUp = () => {
      if (storageKey && lastWidthsRef.current) {
        try {
          localStorage.setItem(STORAGE_PREFIX + storageKey, JSON.stringify(lastWidthsRef.current));
        } catch (_) {}
      }
      resizingRef.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [colWidths, columns, storageKey]);

  // --- Filters ---
  const [filters, setFilters] = useState({});
  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  // --- Sorting ---
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'

  const handleSort = useCallback((colKey) => {
    setSortCol((prev) => {
      if (prev === colKey) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return colKey;
      }
      setSortDir('asc');
      return colKey;
    });
  }, []);

  // --- Filtered & sorted data ---
  const processedData = useMemo(() => {
    let result = [...data];

    // Apply filters
    for (const col of columns) {
      const filterVal = filters[col.key];
      if (filterVal === undefined || filterVal === '' || filterVal === 'all') continue;

      if (col.filterType === 'text') {
        const term = filterVal.trim().toLowerCase();
        if (!term) continue;
        const accessor = col.filterAccessor || ((row) => row[col.key]);
        result = result.filter((row) => {
          const val = accessor(row);
          return val != null && String(val).toLowerCase().includes(term);
        });
      } else if (col.filterType === 'select') {
        const accessor = col.filterAccessor || ((row) => row[col.key]);
        result = result.filter((row) => accessor(row) === filterVal);
      } else if (col.filterType === 'number') {
        const num = parseFloat(filterVal);
        if (isNaN(num)) continue;
        const accessor = col.filterAccessor || ((row) => row[col.key]);
        result = result.filter((row) => {
          const val = accessor(row);
          return val != null && Number(val) >= num;
        });
      } else if (col.filterType === 'date') {
        const filterDate = new Date(filterVal);
        if (isNaN(filterDate.getTime())) continue;
        const accessor = col.filterAccessor || ((row) => row[col.key]);
        const filterTs = filterDate.setHours(0, 0, 0, 0);
        result = result.filter((row) => {
          const val = accessor(row);
          if (val == null) return false;
          const rowDate = val instanceof Date ? val : new Date(val);
          return !isNaN(rowDate.getTime()) && rowDate.setHours(0, 0, 0, 0) >= filterTs;
        });
      }
    }

    // Apply sorting
    if (sortCol) {
      const col = columns.find((c) => c.key === sortCol);
      if (col) {
        const accessor = col.sortAccessor || col.filterAccessor || ((row) => row[sortCol]);
        result.sort((a, b) => {
          let va = accessor(a);
          let vb = accessor(b);
          if (va == null) va = '';
          if (vb == null) vb = '';
          if (typeof va === 'number' && typeof vb === 'number') {
            return sortDir === 'asc' ? va - vb : vb - va;
          }
          const sa = String(va).toLowerCase();
          const sb = String(vb).toLowerCase();
          if (sa < sb) return sortDir === 'asc' ? -1 : 1;
          if (sa > sb) return sortDir === 'asc' ? 1 : -1;
          return 0;
        });
      }
    }

    return result;
  }, [data, columns, filters, sortCol, sortDir]);

  // --- Render ---
  const getRowKey = typeof rowKey === 'function' ? rowKey : (row) => row[rowKey];
  const totalWidth = columns.reduce((sum, col) => sum + (colWidths[col.key] || col.width || 150), 0);

  return (
    <div className={cn('relative w-full overflow-auto', className)}>
      <table
        className={cn('caption-bottom text-sm border-collapse', isDark ? 'text-slate-200' : 'text-slate-900')}
        style={{ tableLayout: 'fixed', width: Math.max(totalWidth, 100) + 'px' }}
      >
        <colgroup>
          {columns.map((col) => (
            <col key={col.key} style={{ width: (colWidths[col.key] || col.width || 150) + 'px' }} />
          ))}
        </colgroup>

        <thead className={cn('[&_tr]:border-b', isDark ? 'bg-slate-950' : 'bg-slate-50')}>
          {/* Header row */}
          <tr className={cn('border-b transition-colors', isDark ? 'border-slate-800' : 'border-slate-200')}>
            {columns.map((col) => (
              <th
                key={col.key}
                className={cn(
                  'h-10 px-2 text-left align-middle font-medium text-sm relative select-none',
                  isDark ? 'text-slate-200' : 'text-slate-600',
                  col.sortable && 'cursor-pointer hover:text-slate-900 dark:hover:text-white'
                )}
                onClick={col.sortable ? () => handleSort(col.key) : undefined}
              >
                <span className="flex items-center gap-1">
                  {col.label}
                  {col.sortable && sortCol === col.key && (
                    sortDir === 'asc'
                      ? <ArrowUp className="w-3 h-3" />
                      : <ArrowDown className="w-3 h-3" />
                  )}
                  {col.sortable && sortCol !== col.key && (
                    <ArrowUpDown className="w-3 h-3 opacity-30" />
                  )}
                </span>
                {/* Resize handle: thin visible line, drag area */}
                <div
                  className={cn(
                    'absolute right-0 top-0 h-full w-[6px] cursor-col-resize z-10 flex justify-center',
                    'hover:bg-sky-500/30 active:bg-sky-500/50'
                  )}
                  onMouseDown={(e) => handleMouseDown(e, col.key)}
                  onClick={(e) => e.stopPropagation()}
                  title="Spaltenbreite anpassen"
                >
                  <span className={cn('w-px h-full shrink-0', isDark ? 'bg-slate-500' : 'bg-slate-300')} aria-hidden />
                </div>
              </th>
            ))}
          </tr>

          {/* Filter row */}
          {columns.some((c) => c.filterType && c.filterType !== 'none') && (
            <tr className={cn('border-b', isDark ? 'border-slate-800 bg-slate-800/50' : 'border-slate-200 bg-slate-100/80')}>
              {columns.map((col) => (
                <td key={col.key} className="p-1">
                  {col.filterType === 'text' && (
                    <Input
                      placeholder={col.filterPlaceholder || `Filter…`}
                      value={filters[col.key] || ''}
                      onChange={(e) => updateFilter(col.key, e.target.value)}
                      className={cn(
                        'h-8 text-xs',
                        isDark
                          ? 'bg-slate-900 border-slate-600 text-slate-200 placeholder:text-slate-500'
                          : 'bg-white border-slate-300'
                      )}
                    />
                  )}
                  {col.filterType === 'select' && (
                    <Select
                      value={filters[col.key] || 'all'}
                      onValueChange={(v) => updateFilter(col.key, v === 'all' ? '' : v)}
                    >
                      <SelectTrigger
                        className={cn(
                          'h-8 text-xs',
                          isDark ? 'bg-slate-900 border-slate-600 text-slate-200' : 'bg-white border-slate-300'
                        )}
                      >
                        <SelectValue placeholder={col.label} />
                      </SelectTrigger>
                      <SelectContent className={isDark ? 'bg-slate-900 border-slate-700' : ''}>
                        <SelectItem value="all">Alle</SelectItem>
                        {(col.filterOptions || []).map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {col.filterType === 'number' && (
                    <Input
                      type="number"
                      min={0}
                      placeholder={col.filterPlaceholder || 'Min'}
                      value={filters[col.key] ?? ''}
                      onChange={(e) => updateFilter(col.key, e.target.value === '' ? '' : e.target.value)}
                      className={cn(
                        'h-8 w-full text-xs',
                        isDark
                          ? 'bg-slate-900 border-slate-600 text-slate-200 placeholder:text-slate-500'
                          : 'bg-white border-slate-300'
                      )}
                    />
                  )}
                  {col.filterType === 'date' && (
                    <Input
                      type="date"
                      value={filters[col.key] ?? ''}
                      onChange={(e) => updateFilter(col.key, e.target.value || '')}
                      className={cn(
                        'h-8 w-full text-xs',
                        isDark
                          ? 'bg-slate-900 border-slate-600 text-slate-200 placeholder:text-slate-500'
                          : 'bg-white border-slate-300'
                      )}
                    />
                  )}
                </td>
              ))}
            </tr>
          )}
        </thead>

        <tbody className="[&_tr:last-child]:border-0">
          {loading ? (
            <tr>
              <td
                colSpan={columns.length}
                className={cn('h-24 text-center', isDark ? 'text-slate-400' : 'text-slate-600')}
              >
                <Loader2 className="w-5 h-5 animate-spin mx-auto" /> Lade…
              </td>
            </tr>
          ) : processedData.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className={cn('h-24 text-center', isDark ? 'text-slate-400' : 'text-slate-600')}
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            processedData.map((row) => (
              <tr
                key={getRowKey(row)}
                className={cn(
                  'border-b transition-colors',
                  isDark ? 'border-slate-800 hover:bg-slate-800/50' : 'border-slate-200 hover:bg-slate-50',
                  onRowClick && 'cursor-pointer'
                )}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={cn(
                      'p-2 align-middle overflow-hidden text-ellipsis whitespace-nowrap',
                      isDark ? 'text-slate-300' : 'text-slate-700'
                    )}
                    title={col.cellTitle ? col.cellTitle(row) : undefined}
                  >
                    {col.render ? col.render(row) : (row[col.key] ?? '—')}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
